/**
 * Homework prompt formatter · slot_homework_content_engineering HW.3 follow-up · 18 MAY 2026.
 *
 * Converts plain-text-with-markers prompt в structured HTML:
 *   - `<strong>X:</strong>` heading line → `<h3 class="hw-section">X</h3>`
 *   - Bullet lines (starting with `•` or `-`) → `<ul class="hw-list"><li>...</li></ul>`
 *   - Multi-line bullets (continuation lines indented) merge into one item
 *   - Plain paragraphs (separated by blank lines) wrap as `<p>`
 *   - Surrounding `"..."` quotes on bullet items stripped (YAML leftovers)
 *
 * Called от Ticket.tsx homework body adapter · ContentRenderer renders output
 * via dangerouslySetInnerHTML. Source HOMEWORK entries в `app/src/data/homework.ts`
 * stay unchanged — formatter applies cosmetic structure at render time only.
 *
 * Tested separately в `__tests__/homework-format.test.ts` для unit coverage.
 */

const stripQuotes = (s: string): string =>
  s.replace(/^["'«](.*)["'»]$/s, '$1').trim();

/**
 * Parse a block of lines into bullet items, merging continuation lines
 * (indented OR non-bullet) into the preceding bullet.
 */
function parseBullets(block: string): string[] {
  const lines = block.split('\n');
  const items: string[] = [];
  let current = '';
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^[•-]\s+/.test(trimmed)) {
      // New bullet · flush previous.
      if (current) items.push(current.trim());
      current = trimmed.replace(/^[•-]\s+/, '');
    } else {
      // Continuation line · append к current с space separator.
      current = current ? `${current} ${trimmed}` : trimmed;
    }
  }
  if (current) items.push(current.trim());
  return items.map(stripQuotes);
}

/**
 * Format one logical block (already split by `\n\n+`).
 * Recursion handles `<strong>X:</strong>` headers с body following on same block.
 */
function formatBlock(block: string): string {
  const trimmed = block.trim();
  if (!trimmed) return '';

  // 1. Block starts with <strong>...:</strong> → emit <h3> + recurse on rest.
  const headerMatch = trimmed.match(/^<strong>([^<]+?):?<\/strong>\s*\n?([\s\S]*)$/);
  if (headerMatch) {
    const headerText = headerMatch[1]!.trim();
    const rest = headerMatch[2]!.trim();
    return `<h3 class="hw-section">${headerText}</h3>${rest ? formatBlock(rest) : ''}`;
  }

  // 2. Block looks like a bullet list (any line starts with • or -).
  const lines = trimmed.split('\n');
  const hasAnyBullet = lines.some((l) => /^[•-]\s+/.test(l.trim()));
  if (hasAnyBullet) {
    const items = parseBullets(trimmed);
    if (items.length > 0) {
      return `<ul class="hw-list">${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
    }
  }

  // 3. Plain paragraph · keep inline line breaks as spaces (newlines are layout wrap, not semantic).
  return `<p>${trimmed.replace(/\n+/g, ' ')}</p>`;
}

/**
 * Convert the raw prompt text into structured HTML for ContentRenderer.
 */
export function formatHomeworkPrompt(raw: string): string {
  const blocks = raw.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  return blocks.map(formatBlock).join('');
}
